<template>
  <div
    class="designer-canvas"
    ref="outerWrapperRef"
    :class="{ 'readonly-mode': isReadonly }"
    :style="{
      width: '100%',
      height: canvasHeightPx + 'px',
    }"
  >
    <div ref="canvasRef" class="canvas-wrapper relative overflow-hidden">
      <DesignerLabelImage
        ref="labelImage"
        :image-src="designerStore.labelImage.imageSrc"
        v-model:width="designerStore.labelImage.width"
        v-model:height="designerStore.labelImage.height"
        v-model:positionX="designerStore.labelImage.positionX"
        v-model:positionY="designerStore.labelImage.positionY"
        :fit="designerStore?.labelImage?.fit ?? 'contain'"
      />

      <DesignerDieCutShape
        ref="labelShape"
        :type="designerStore.dieCutMask.type"
        v-model:width="designerStore.dieCutMask.width"
        v-model:height="designerStore.dieCutMask.height"
        v-model:radiusX="designerStore.dieCutMask.radiusX"
        v-model:radiusY="designerStore.dieCutMask.radiusY"
        v-model:positionX="designerStore.dieCutMask.positionX"
        v-model:positionY="designerStore.dieCutMask.positionY"
        v-model:z-index="designerStore.dieCutMask.zIndex"
        v-model:bleed-offset="designerStore.dieCutMask.bleedOffset"
        v-model:svg="designerStore.dieCutSvg"
      />

      <DesignerTextLayer
        v-for="layer in designerStore.textLayers"
        :key="layer.id"
        :id="layer.id"
        :text="layer.text"
        :font="layer.font"
        :fontWeight="layer.fontWeight"
        :fontSize="layer.fontSize"
        :color="layer.color"
        :align="layer.align"
        :positionX="layer.positionX"
        :positionY="layer.positionY"
        :width="layer.width"
        :height="layer.height"
        :zIndex="layer.zIndex"
        @update:positionX="layer.positionX = $event"
        @update:positionY="layer.positionY = $event"
        @update:width="layer.width = $event"
        @update:height="layer.height = $event"
      />
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, onBeforeUnmount, computed } from 'vue'
import DesignerLabelImage from '@/components/designer/canvas/DesignerLabelImage.vue'
import DesignerDieCutShape from '@/components/designer/canvas/DesignerDieCutShape.vue'
import DesignerTextLayer from '@/components/designer/canvas/DesignerTextLayer.vue'
import { useDesignerStore } from '@/stores/designerStore.ts'
import { useAdminDesignerStore } from '@/stores/adminDesignerStore.ts'
import { isEmbedded } from '@/utils/embed'

const designerStore = useDesignerStore()
const adminStore = useAdminDesignerStore()

const isReadonly = computed(() => adminStore.readonly)

const canvasRef = ref<HTMLElement | null>(null)
const outerWrapperRef = ref<HTMLElement | null>(null)

const canvasWidthPx = ref(0)
const canvasHeightPx = ref(0)

/**
 * Čakáme, kým canvas-wrapper prestane byť width=0
 */
function waitForCanvasReady() {
  return new Promise(resolve => {
    const i = setInterval(() => {
      const rect = canvasRef.value?.getBoundingClientRect()
      if (rect && rect.width > 0 && rect.height > 0) {
        clearInterval(i)
        console.log("[DC] Canvas ready:", rect.width, rect.height)
        resolve(true)
      }
    }, 30)
  })
}

function updateSize() {
  if (!outerWrapperRef.value) return

  const w = outerWrapperRef.value.clientWidth
  if (w === 0) return

  canvasWidthPx.value = w
  canvasHeightPx.value =
    w * (designerStore.canvasSizeInMm.height / designerStore.canvasSizeInMm.width)

  designerStore.bumpScale()
}

onMounted(async () => {
  await nextTick()

  const embedded = isEmbedded()

  // 🟡 1) Čakáme, kým canvas-wrapper má skutočný rozmer
  await waitForCanvasReady()

  // 🟡 2) Teraz môžeme prepočítať veľkosť
  updateSize()

  // 🟡 3) AŽ TERAZ nastavujeme canvasRef v store
  if (canvasRef.value) {
    designerStore.setCanvasRef(canvasRef.value)

    await nextTick()
    designerStore.bumpScale()
  }

  // 🟡 FE (public) → ResizeObserver
  if (!embedded && outerWrapperRef.value) {
    const resizeObserver = new ResizeObserver(() => {
      console.log("[DC] ResizeObserver FIRED")
      updateSize()
    })
    resizeObserver.observe(outerWrapperRef.value)
  }

  // 🟡 ADMIN → update pri prepnutí tabu
  if (embedded) {
    document.addEventListener('shown.bs.tab', ev => {
      const href = (ev.target as HTMLElement)?.getAttribute?.('href')
      if (href && /#primarytab-designer$/.test(href)) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            console.log("[DC] Admin tab shown → updateSize()")
            updateSize()
          })
        )
      }
    })
  }
})

// Expose updateSize for parent components (e.g., DesignerPreview)
defineExpose({
  updateSize
})
</script>

<style scoped lang="scss">
.designer-canvas {
  background: #ffffff;
  position: relative;
  box-shadow:
    5px 2px 2px rgba(98, 76, 29, 0.04),
    3px 1px 2px rgba(98, 76, 29, 0.12),
    1px 0px 1px rgba(98, 76, 29, 0.2),
    0px 0px 1px rgba(98, 76, 29, 0.24);
  border-radius: 32px;

  .canvas-wrapper {
    box-sizing: border-box;
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    top: 8px;
    background: #ffffff;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 28px;
  }
  
  // Readonly mode - disable all interactions
  &.readonly-mode {
    .canvas-wrapper {
      pointer-events: none;
    }
    
    :deep(.resize-handle) {
      display: none !important;
    }
  }
}
</style>
